import React, { useState } from "react";
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Button,
  Pagination,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Tooltip,
  useDisclosure,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner,
} from "@heroui/react";
import {
  Eye,
  MoreVertical,
  ArrowUpRight,
  Printer,
  Ban,
  CheckCircle2,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import { Order } from "@/types/orders";
import { ordersService } from "@/services/ordersService";
import { formatDate, formatTime, formatCurrency } from "@/utils/formatters";
import { OrderStatus } from "@/types/config";

interface OrdersTableProps {
  orders: Order[];
  orderStatuses: OrderStatus[];
  loading: boolean;
  pagination: {
    page: number;
    pageSize: number;
    total: number;
  };
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onOrderUpdated: () => void;
}

export default function OrdersTable({
  orders,
  orderStatuses,
  loading,
  pagination,
  onPageChange,
  onPageSizeChange,
  onOrderUpdated,
}: OrdersTableProps) {
  const navigate = useNavigate();
  const [error, setError] = useState("");

  // Status update modal
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [selectedStatusId, setSelectedStatusId] = useState<number | null>(null);
  const [statusUpdateNotes, setStatusUpdateNotes] = useState("");
  const [updating, setUpdating] = useState(false);

  // Payment update modal
  const {
    isOpen: isPaymentModalOpen,
    onOpen: onPaymentModalOpen,
    onClose: onPaymentModalClose,
  } = useDisclosure();
  const [processingPayment, setProcessingPayment] = useState(false);

  const handleViewOrder = (orderId: number) => {
    navigate(`/orders/${orderId}`);
  };

  const handleStatusChange = (order: Order) => {
    setSelectedOrder(order);
    setSelectedStatusId(order.status_id);
    setStatusUpdateNotes("");
    onOpen();
  };

  const handleUpdateStatus = async () => {
    if (!selectedOrder || !selectedStatusId) return;

    try {
      setUpdating(true);
      setError("");

      await ordersService.updateOrderStatus(selectedOrder.id, {
        status_id: selectedStatusId,
        notes: statusUpdateNotes,
      });

      onClose();
      onOrderUpdated();
    } catch (err) {
      console.error("Error updating order status:", err);
      setError("Failed to update order status");
    } finally {
      setUpdating(false);
    }
  };

  const handlePaymentUpdate = (order: Order) => {
    setSelectedOrder(order);
    onPaymentModalOpen();
  };

  const handleMarkAsPaid = async () => {
    if (!selectedOrder) return;

    try {
      setProcessingPayment(true);
      setError("");

      await ordersService.updateOrderPayment(selectedOrder.id, {
        is_paid: true,
      });

      onPaymentModalClose();
      onOrderUpdated();
    } catch (err) {
      console.error("Error updating payment status:", err);
      setError("Failed to update payment status");
    } finally {
      setProcessingPayment(false);
    }
  };

  const renderStatusChip = (statusName: string) => {
    let color: "primary" | "warning" | "success" | "secondary" | "danger" | "default" | undefined;

    switch (statusName.toLowerCase()) {
      case "new":
        color = "primary";
        break;
      case "processing":
        color = "warning";
        break;
      case "ready":
        color = "success";
        break;
      case "out_for_delivery":
        color = "secondary";
        break;
      case "delivered":
      case "picked_up":
        color = "success";
        break;
      case "cancelled":
        color = "danger";
        break;
      default:
        color = "default";
    }

    return (
      <Chip color={color} size="sm" variant="flat">
        {statusName}
      </Chip>
    );
  };

  const renderCell = (order: Order, columnKey: React.Key) => {
    switch (columnKey) {
      case "order_number":
        return (
          <Link
            className="text-primary hover:underline font-medium"
            to={`/orders/${order.id}`}
          >
            #{order.order_number}
          </Link>
        );
      case "customer":
        return (
          <div>
            <div className="font-medium">{order.customer_name}</div>
            <div className="text-sm text-default-500">
              {order.customer_phone}
            </div>
          </div>
        );
      case "date":
        return (
          <div>
            <div>{formatDate(order.created_at)}</div>
            <div className="text-sm text-default-500">
              {formatTime(order.created_at)}
            </div>
          </div>
        );
      case "order_type":
        return (
          <Chip size="sm" variant="flat">
            {order.order_type === "pickup" ? "Pickup" : "Delivery"}
          </Chip>
        );
      case "status":
        return renderStatusChip(order.status_name);
      case "payment":
        return (
          <Chip
            color={order.is_paid ? "success" : "warning"}
            size="sm"
            variant="flat"
          >
            {order.is_paid ? "Paid" : "Unpaid"}
          </Chip>
        );
      case "total":
        return (
          <span className="font-medium">{formatCurrency(order.total)}</span>
        );
      case "actions":
        return (
          <div className="flex items-center justify-end">
            <Tooltip content="View Details">
              <Button
                isIconOnly
                size="sm"
                variant="light"
                onPress={() => handleViewOrder(order.id)}
              >
                <Eye size={18} />
              </Button>
            </Tooltip>
            <Dropdown>
              <DropdownTrigger>
                <Button isIconOnly size="sm" variant="light">
                  <MoreVertical size={18} />
                </Button>
              </DropdownTrigger>
              <DropdownMenu aria-label="Order Actions">
                <DropdownItem
                  key="view"
                  startContent={<ArrowUpRight size={16} />}
                  onPress={() => handleStatusChange(order)}
                >
                  Update Status
                </DropdownItem>
                {!order.is_paid ? (
                  <DropdownItem
                    key="mark-as-paid"
                    startContent={<CheckCircle2 size={16} />}
                    onPress={() => handlePaymentUpdate(order)}
                  >
                    Mark as Paid
                  </DropdownItem>
                ) : null}
                <DropdownItem key="print-receipt" startContent={<Printer size={16} />}>
                  Print Receipt
                </DropdownItem>
                {order.status_name.toLowerCase() !== "cancelled" ? (
                  <DropdownItem
                    key="cancel"
                    className="text-danger"
                    startContent={<Ban size={16} />}
                  >
                    Cancel Order
                  </DropdownItem>
                ) : null}
              </DropdownMenu>
            </Dropdown>
          </div>
        );
      default:
        const value = order[columnKey as keyof Order];
        if (Array.isArray(value)) {
          return value.map((item, index) => (
            <span key={index}>{String(item)}</span>
          ));
        }
        return value as React.ReactNode;
    }
  };

  if (!pagination) return null;

  const totalPages = Math.ceil(pagination.total / pagination.pageSize);

  return (
    <>
      <div className="max-w-full overflow-x-auto">
        <Table
          aria-label="Orders table"
          bottomContent={
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <span className="text-small text-default-400">
                  Rows per page:
                </span>
                <select
                  className="bg-transparent text-small text-default-400 border border-default-400 rounded-md px-2 py-1"
                  value={pagination.pageSize}
                  onChange={(e) => onPageSizeChange(Number(e.target.value))}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
              <Pagination
                showControls
                color="primary"
                page={pagination.page}
                total={totalPages}
                onChange={onPageChange}
              />
            </div>
          }
          classNames={{
            wrapper: "min-h-[222px]",
          }}
        >
          <TableHeader>
            <TableColumn key="order_number">ORDER #</TableColumn>
            <TableColumn key="customer">CUSTOMER</TableColumn>
            <TableColumn key="date">DATE</TableColumn>
            <TableColumn key="order_type">TYPE</TableColumn>
            <TableColumn key="status">STATUS</TableColumn>
            <TableColumn key="payment">PAYMENT</TableColumn>
            <TableColumn key="total">TOTAL</TableColumn>
            <TableColumn key="actions" className="text-end">
              ACTIONS
            </TableColumn>
          </TableHeader>
          <TableBody
            emptyContent={
              <div className="py-8 text-center">
                <p className="text-default-500">No orders found</p>
              </div>
            }
            items={orders}
            loadingContent={loading ? <Spinner /> : null}
            loadingState={loading ? "loading" : "idle"}
          >
            {(order) => (
              <TableRow key={order.id}>
                {(columnKey) => (
                  <TableCell>{renderCell(order, columnKey)}</TableCell>
                )}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Status Update Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>Update Order Status</ModalHeader>
              <ModalBody>
                {selectedOrder && (
                  <div className="space-y-4">
                    <div>
                      <div className="font-medium">
                        Order #{selectedOrder.order_number}
                      </div>
                      <div className="text-sm text-default-500">
                        {selectedOrder.customer_name}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium">
                        Current Status
                      </label>
                      <div>
                        {renderStatusChip(
                          selectedOrder.status_name
                        )}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium">New Status</label>
                      <select
                        className="w-full p-2 border rounded-md bg-content1"
                        value={selectedStatusId || ""}
                        onChange={(e) =>
                          setSelectedStatusId(Number(e.target.value))
                        }
                      >
                        {orderStatuses.map((status) => (
                          <option key={status.id} value={status.id}>
                            {status.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium">
                        Notes (Optional)
                      </label>
                      <textarea
                        className="w-full p-2 border rounded-md bg-content1"
                        placeholder="Add notes about this status change"
                        rows={3}
                        value={statusUpdateNotes}
                        onChange={(e) => setStatusUpdateNotes(e.target.value)}
                      />
                    </div>

                    {error && (
                      <div className="text-danger text-sm">{error}</div>
                    )}
                  </div>
                )}
              </ModalBody>
              <ModalFooter>
                <Button variant="flat" onPress={onClose}>
                  Cancel
                </Button>
                <Button
                  color="primary"
                  isLoading={updating}
                  onPress={handleUpdateStatus}
                >
                  Update Status
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      {/* Payment Update Modal */}
      <Modal isOpen={isPaymentModalOpen} onClose={onPaymentModalClose}>
        <ModalContent>
          {() => (
            <>
              <ModalHeader>Mark Order as Paid</ModalHeader>
              <ModalBody>
                {selectedOrder && (
                  <div className="space-y-4">
                    <div>
                      <div className="font-medium">
                        Order #{selectedOrder.order_number}
                      </div>
                      <div className="text-sm text-default-500">
                        {selectedOrder.customer_name}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span>Total Amount:</span>
                        <span className="font-medium">
                          {formatCurrency(selectedOrder.total)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span>Payment Method:</span>
                        <span>{selectedOrder.payment_method_name}</span>
                      </div>
                    </div>

                    <div className="bg-default-100 p-3 rounded-md">
                      <p className="text-sm">
                        This will mark the order as paid. This action cannot be
                        undone.
                      </p>
                    </div>

                    {error && (
                      <div className="text-danger text-sm">{error}</div>
                    )}
                  </div>
                )}
              </ModalBody>
              <ModalFooter>
                <Button variant="flat" onPress={onPaymentModalClose}>
                  Cancel
                </Button>
                <Button
                  color="success"
                  isLoading={processingPayment}
                  onPress={handleMarkAsPaid}
                >
                  Mark as Paid
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
}
